import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import './App.css';

// ── Shell: always rendered, must be in the main bundle ──────────────────────
import Navbar from './components/Navbar';
import Home from './components/Home';
import Footer from './components/Footer';
import NewsletterPopup from './components/NewsletterPopup';
import AccessibilityTools from './components/AccessibilityTools';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import ScrollRefresh from './components/motion/ScrollRefresh';


// ── Lazy: heavy AI widget, always mounted but deferred ───────────────────────
const TravelAIAssistant = lazy(() => import('./components/TravelAIAssistant/TravelAIAssistant'));

// ── Lazy: route components ───────────────────────────────────────────────────
// General pages
const About            = lazy(() => import('./components/About'));
const Enquire          = lazy(() => import('./components/Enquire'));
const Contact          = lazy(() => import('./components/Contact'));
const Destinations     = lazy(() => import('./components/Destinations'));
const DestinationDetails = lazy(() => import('./components/DestinationDetails'));
const Excursions       = lazy(() => import('./components/Excursions'));
const ExcursionLocation = lazy(() => import('./components/ExcursionLocation'));
const ExcursionDetail  = lazy(() => import('./components/ExcursionDetail'));
const ExcursionBookingForm = lazy(() => import('./components/ExcursionBookingForm'));
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions'));
const FAQ              = lazy(() => import('./components/FAQ'));
const CustomerReviews  = lazy(() => import('./components/CustomerReviews'));
const Blog             = lazy(() => import('./components/Blog'));
const BlogArticle      = lazy(() => import('./components/BlogArticle'));
const PaymentResult    = lazy(() => import('./components/PaymentResult'));

// Safari pages
const Safaris          = lazy(() => import('./components/Safaris'));
const SafariTours      = lazy(() => import('./components/SafariTours'));
// One shared, data-driven country template + package page replace the 5 separate
// country components (KenyaSafari/TanzaniaSafari/RwandaSafari/SouthAfrica/ZanzibarSafari).
const CountrySafari    = lazy(() => import('./components/CountrySafari'));
const SafariPackage    = lazy(() => import('./components/SafariPackage'));
const RwandaSafariShowcase = lazy(() => import('./components/RwandaSafariShowcase'));

// Booking forms
const BookingForm      = lazy(() => import('./components/BookingForm'));
// One unified safari booking-enquiry form for all countries (replaced 5 separate forms).
const SafariBookingForm = lazy(() => import('./components/SafariBookingForm'));
const HotelBookingForm = lazy(() => import('./components/HotelBookingForm'));

// Hotel & car hire
const HotelDirectory   = lazy(() => import('./components/HotelDirectory'));
const HotelDetail      = lazy(() => import('./components/HotelDetail'));
const CarHire          = lazy(() => import('./components/CarHire'));
const CarDetail        = lazy(() => import('./components/CarDetail'));

// Pricing
const Pricelist        = lazy(() => import('./components/Pricelist'));

function App() {
  return (
    <Router>
      <div className="App">
        <a href="#main-content" className="skip-to-content">Skip to main content</a>
        <ScrollToTop />
        <ScrollRefresh />
        <Navbar />
        <NewsletterPopup />
        <AccessibilityTools />
        <CookieConsent />
        <Suspense fallback={null}>
          <TravelAIAssistant />
        </Suspense>
        {/* <TawkChat /> */}

        <main id="main-content">
          <Suspense fallback={<div style={{ minHeight: '60vh' }} />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/payment/success" element={<PaymentResult mode="success" />} />
              <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />
              <Route path="/enquire" element={<Enquire />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/safaris" element={<Safaris />} />
              {/* One shared template for every country + a dedicated package page */}
              <Route path="/safaris/:country" element={<CountrySafari />} />
              <Route path="/safaris/:country/:slug" element={<SafariPackage />} />
              <Route path="/pricelist" element={<Pricelist />} />
              <Route path="/car-hire" element={<CarHire />} />
              <Route path="/car-hire/:slug" element={<CarDetail />} />
              <Route path="/hotel-booking" element={<HotelBookingForm />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route path="/booking-form/:carId" element={<BookingForm />} />
              <Route path="/destinations" element={<Destinations />} />
              <Route path="/destinations/:country/:destination" element={<DestinationDetails />} />
              <Route path="/excursions" element={<Excursions />} />
              <Route path="/excursions/:location" element={<ExcursionLocation />} />
              <Route path="/excursions/:location/:slug" element={<ExcursionDetail />} />
              <Route path="/excursions/:location/:slug/book" element={<ExcursionBookingForm />} />
              <Route path="/safaari" element={<SafariTours />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/rwandashow" element={<RwandaSafariShowcase />} />
              <Route path="/reviews" element={<CustomerReviews />} />
              <Route path="/directory" element={<HotelDirectory />} />
              <Route path="/directory/:slug" element={<HotelDetail />} />
              <Route path="/safaris/rwanda/book/:tourId" element={<SafariBookingForm country="Rwanda" />} />
              <Route path="/safaris/kenya/book/:id" element={<SafariBookingForm country="Kenya" />} />
              <Route path="/safaris/tanzania/book/:packageId" element={<SafariBookingForm country="Tanzania" />} />
              <Route path="/safaris/southafrica/booking/:packageId" element={<SafariBookingForm country="South Africa" />} />
              <Route path="/safaris/zanzibar/book/:tourId" element={<SafariBookingForm country="Zanzibar" />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
